"use client";

import Link from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { sectionScrollState } from "./sectionScrollState";

export type SectionTab = {
  /**
   * Path segment under the parent route. `null` is the index route
   * (i.e. the parent itself, like `/climate` for the climate section).
   */
  segment: string | null;
  /**
   * Extra `useSelectedLayoutSegment()` values that should also mark this
   * tab active, beyond `segment` itself. For a section like
   * /property-taxes whose hub (`segment: null`, `/property-taxes`) and a
   * kept-alive 200 alias (`/property-taxes/by-community`, segment
   * `"by-community"`) render the same figure: the headline tab points at
   * the hub but should still read as current when a reader lands on the
   * cited alias URL. Default behaviour (no aliases) is unchanged for
   * every other section.
   */
  aliasSegments?: (string | null)[];
  href: string;
  /** Two-digit-style counter shown ahead of the label, e.g. "01". Optional —
   *  omitted in the Epic 021 redesign, which drops case-file numbering. */
  num?: string;
  label: string;
  /**
   * "data" (default) for primary-figure tabs; "editorial" for rebuttal /
   * commentary pages that aren't a data figure. Editorial tabs render with
   * a left divider and a muted accent on `num` so a reader can tell them
   * apart from data tabs without reading labels (see SC-008).
   */
  kind?: "data" | "editorial";
};

type Props = {
  /** Ordered list of tabs the strip should render. */
  tabs: SectionTab[];
  /** Accessible label for the nav element, e.g. "Climate sections". */
  ariaLabel: string;
};

/**
 * Sticky tab strip that pins under the site header and lets the reader
 * jump between sibling routes inside a section (e.g. the figures of
 * /climate, the chapters of /water). Behaviour:
 *
 *   - Pins below the header at `var(--site-header-h)` and shrinks its
 *     padding via the `data-stuck` attribute once flush.
 *   - On click, snaps the page so the strip lands flush under the
 *     header — both when the reader is above the strip (scroll down to
 *     pin) and when they're deep in a section (scroll up to a fresh
 *     start). Computes the target off the strip's previous sibling
 *     so sticky pinning doesn't poison the math.
 *   - Re-snaps after the new route segment commits, in case the new
 *     route's content height shifted the target between click and
 *     navigation.
 *
 * The component renders a `cs-tabs` `<nav>` and per-tab `cs-tab` links;
 * style hooks live in globals.css.
 */
export function SectionTabs({ tabs, ariaLabel }: Props) {
  const active = useSelectedLayoutSegment();
  const navRef = useRef<HTMLElement>(null);

  // Keep --site-header-h in sync with the actual sticky header height
  // so the tab strip can sit flush against its bottom edge across
  // breakpoints. Also toggle data-stuck on the nav whenever it's
  // pinned, so CSS can collapse the strip's padding while pinned.
  useEffect(() => {
    const header = document.querySelector("header");
    const nav = navRef.current;
    if (!header || !nav) return;

    let headerH = Math.ceil(header.getBoundingClientRect().height);
    const syncHeaderH = () => {
      headerH = Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        "--site-header-h",
        `${headerH}px`,
      );
      updateStuck();
    };
    const updateStuck = () => {
      const top = nav.getBoundingClientRect().top;
      const next = top <= headerH + 0.5 ? "true" : "false";
      if (nav.dataset.stuck !== next) nav.dataset.stuck = next;
    };

    syncHeaderH();
    const ro = new ResizeObserver(syncHeaderH);
    ro.observe(header);
    window.addEventListener("scroll", updateStuck, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", updateStuck);
    };
  }, []);

  // Compute where to land so the tab strip sits flush below the header.
  // We measure off the nav's previous sibling (a non-sticky element),
  // not the nav itself: when the nav is currently sticky-pinned, both
  // `getBoundingClientRect().top + scrollY` and the offsetTop chain
  // return the *painted* sticky position rather than the in-flow
  // position, which collapses the target to current scrollY and the
  // scroll becomes a no-op. The previous sibling never sticks, so its
  // bottom edge is always the nav's true in-flow top.
  const computeTargetY = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return null;
    const sibling = nav.previousElementSibling;
    if (!(sibling instanceof HTMLElement)) return null;
    const headerH =
      parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--site-header-h")
          .trim(),
      ) || 96;
    const rect = sibling.getBoundingClientRect();
    const navInFlowTop = rect.top + window.scrollY + rect.height;
    return navInFlowTop - headerH;
  }, []);

  // Land at the top of the new section on tab click. Two-step:
  //   1. Smooth-scroll on click, before the route transition begins.
  //      `scroll={false}` on the Link preserves the position through
  //      navigation so Next doesn't fight us.
  //   2. After the new segment commits, re-issue a smooth scrollTo to
  //      the new target. The browser replaces the in-flight smooth
  //      animation with the new one, so if the segment swap moved the
  //      strip's in-flow position the animation continues smoothly
  //      from wherever it got to, ending at the correct new target.
  const scrollBehavior = (): ScrollBehavior =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth";

  const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const targetY = computeTargetY();
    if (targetY == null) return;
    // Only mark the navigation as tab-driven if a segment change is
    // actually coming. Clicking the already-active tab is a no-op for
    // the segment, so the post-commit effect that consumes the flag
    // would never fire — leaving `tabClicked` stuck `true` and causing
    // unrelated future navigations to skip `RouteScrollTop`'s override.
    const isActiveTab = e.currentTarget.dataset.active === "true";
    if (!isActiveTab) sectionScrollState.tabClicked = true;
    if (Math.abs(window.scrollY - targetY) > 0.5) {
      window.scrollTo({ top: targetY, behavior: scrollBehavior() });
    }
  };

  // Defensive backstop: if the strip unmounts mid-navigation (e.g. the
  // reader leaves the section before the new segment commits), drop any
  // unconsumed flag so the next section's `RouteScrollTop` isn't
  // suppressed by a stale value.
  useEffect(() => {
    return () => {
      sectionScrollState.tabClicked = false;
    };
  }, []);

  const prevActive = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const current = active ?? null;
    if (prevActive.current === undefined) {
      prevActive.current = current;
      return;
    }
    if (prevActive.current === current) return;
    prevActive.current = current;

    // Only re-issue the flush scroll when this segment change came from
    // a tab click — otherwise an unrelated intra-section navigation
    // (header `/water` link from `/water/demand`, in-page anchor link,
    // etc.) would be misread as a tab change and drag the reader
    // partway down the page. `RouteScrollTop` handles those by pinning
    // to the masthead instead.
    if (!sectionScrollState.tabClicked) return;
    sectionScrollState.tabClicked = false;

    const targetY = computeTargetY();
    if (targetY == null) return;
    if (Math.abs(window.scrollY - targetY) > 0.5) {
      window.scrollTo({ top: targetY, behavior: scrollBehavior() });
    }
  }, [active, computeTargetY]);

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className="cs-tabs sticky z-40 border-y border-rule bg-bg/90 backdrop-blur-md"
      style={{ top: "var(--site-header-h, 96px)" }}
    >
      <div className="px-4 sm:px-8 overflow-x-auto">
        <ul className="flex items-stretch w-fit min-w-full sm:mx-auto">
          {tabs.map((tab, i) => {
            const current = active ?? null;
            const isActive =
              current === tab.segment ||
              (tab.aliasSegments?.includes(current) ?? false);
            const kind = tab.kind ?? "data";
            const isEditorial = kind === "editorial";
            // Editorial tabs sit visually apart: a thicker left rule
            // signals "these aren't a data figure, they're commentary".
            // We bump the `border-l` only when the *previous* tab is a
            // data tab (i.e. on the boundary), so consecutive editorial
            // tabs don't stack rules.
            const prevKind = i > 0 ? (tabs[i - 1].kind ?? "data") : null;
            const showEditorialDivider = isEditorial && prevKind === "data";
            return (
              <li key={tab.href} className={showEditorialDivider ? "border-l-2 border-signal/40" : undefined}>
                <Link
                  href={tab.href}
                  scroll={false}
                  onClick={handleTabClick}
                  aria-current={isActive ? "page" : undefined}
                  data-active={isActive ? "true" : "false"}
                  data-kind={kind}
                  className="cs-tab group relative flex items-baseline gap-2 px-3 sm:px-4 py-4 sm:py-5 transition-colors duration-150 hover:bg-bg-2 border-r border-rule last:border-r-0"
                >
                  {tab.num && (
                    <span className="cs-num text-ink-3 group-data-[active=true]:text-signal text-base sm:text-lg flex-none">
                      {tab.num}
                    </span>
                  )}
                  <span className="cs-mono text-[12px] sm:text-[13px] text-ink-3 group-data-[active=true]:text-ink leading-none whitespace-nowrap">
                    {tab.label}
                  </span>
                  <span
                    aria-hidden
                    className="cs-tab-underline pointer-events-none absolute left-0 right-0 bottom-[-1px] h-[3px] bg-signal origin-left scale-x-0 group-data-[active=true]:scale-x-100 transition-transform duration-300"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
